import { useMemo } from 'react';
import {
  ActionableNotification,
  InlineNotification,
  Loading,
  Modal,
} from '@carbon/react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import {
  AtomisedNotifications,
  NotificationGroup,
} from 'components/atomised-notifications';
import { DocumentResponse } from 'types/api-responses';
import { structuredPartiesListDataAtom } from './atoms/structured-parties';
import { UserAddedConfirmedCard } from './user-added-confirmed-card';
import { FlagOverrideModal } from 'components/flag-override-modal';
import { ActivityLog } from './activity-log';
import { ChangeMatchPanel } from './change-match-panel';
import { CanonizePanel } from './canonize-panel';
import { StagedEntityEditorPanel } from './staged-entity-editor-panel';
import { ScoringGlossaryPanel } from './scoring-glossary-panel';
import { AgentDetailsPanel } from './agent-details-panel';
import { PartyHeader } from './party-header';
import { MarkCorrectModal } from './mark-correct-modal';
import { MatchReviewIntro, PartyStatusBanners } from './party-detail-banners';
import { PartyMatchesPane } from './party-matches-pane';
import { partyState } from './party-status';
import { anchorCard, anchorName } from './utils';
import { endSentence } from 'utils/strings';
import { EMPTY_CELL } from 'utils/empty-cell';
// Match Review launches the two-panel Entity Consolidation tearsheet, which
// defaults to deduplication and shows the document image alongside.
import ReconcileTearsheet from './reconcile/match-review-consolidation-tearsheet';
import RowAliasGraphLink from './reconcile/row-alias-graph-link';
import { ALIAS_CLUSTER_GRAPH_SELECTOR } from './reconcile/alias-cluster-graph';
import { buildReconcileCandidates } from './reconcile/candidate-utils';
import { SharedAnchorNotice } from './shared-anchor-notice';
import {
  usePartyActions,
  usePartyActivityFeed,
  usePartyDetail,
  usePartyLiveUpdates,
} from './hooks';
import style from './party-detail.module.scss';
import type {
  StructuredPartyDetail,
  StructuredPartyListItem,
  SuggestionSummary,
} from './types';

type ReadyPartyActions = Extract<
  ReturnType<typeof usePartyActions>,
  { ready: true }
>;

interface PartyDetailProps {
  document: DocumentResponse;
  party: StructuredPartyListItem;
  index: number;
  onMutated: () => void;
}

/**
 * One party's Match Review: its header and actions, its matches, its activity, and the modals and
 * side panels its actions open. Kept in step with changes made elsewhere.
 *
 * @param {PartyDetailProps} props - The document, the party and its position, and the list refresh.
 * @return {JSX.Element} The party's detail.
 */
export function PartyDetail({
  document,
  party,
  index,
  onMutated,
}: PartyDetailProps) {
  const selfHref = party.href;
  const {
    detail,
    loading: detailLoading,
    refreshFailed,
    refetch: refetchDetail,
    show: showDetail,
  } = usePartyDetail(selfHref);
  const partiesListData = useAtomValue(structuredPartiesListDataAtom);
  const allParties = partiesListData?.parties ?? [];

  const validMatches = useMemo(
    () => (detail ? detail.suggestions.filter((s) => s.suggestedEntity) : []),
    [detail]
  );

  const actions = usePartyActions({
    document,
    selfHref,
    detail,
    validMatches,
    refetchDetail,
    onMutated,
  });

  const activity = usePartyActivityFeed({
    documentId: document.id,
    party: detail,
    refreshToken: actions.auditRefreshToken,
  });

  usePartyLiveUpdates({
    channelName: document.notifications?.channel,
    selfHref,
    detail,
    detailLoading,
    ownSubmitInFlight: actions.ownSubmitInFlight,
    onResolutionChanged: (fresh, message, listStale) => {
      // Only an examiner mid-action lost anything; otherwise refresh quietly.
      if (actions.discardOpenWork()) {
        actions.notify('updated-elsewhere', message(), 'info');
      }
      showDetail(fresh);
      if (listStale) onMutated();
      actions.refreshActivity();
    },
    onRefresh: (fresh, listStale) => {
      if (listStale) onMutated();
      if (fresh) showDetail(fresh);
      else refetchDetail();
      actions.refreshActivity();
    },
  });

  const renderParty = () => {
    // A refetch keeps the loaded party, even when it fails, so the spinner and
    // the error are the first load only.
    if (!detail || !actions.ready) {
      return detailLoading ? (
        <Loading withOverlay={false} />
      ) : (
        <div className={style.error}>Unable to load this party.</div>
      );
    }

    const state = partyState(detail);
    const { isRemoved, isUserAdded, hasUnresolvedAddressFlag } = state;
    const { panels, markCorrect, removeModal, rerunModal } = actions;
    const roleSuffix = detail.role ? ` · ${detail.role}` : '';
    const matchesSectionClass = classNames(style.matchesSection, {
      [style.matchesSectionDisabled]: isRemoved,
    });
    return (
      <article className={style.wrapper}>
        <PartyHeader
          title={`Party ${index + 1}${roleSuffix}`}
          detail={detail}
          state={state}
          pending={actions.pending}
          rerunPending={actions.rerunPending}
          changeMatchOpen={panels.changeMatchOpen}
          undoReconcileAvailable={actions.reconcileUndoAvailable}
          hasMatches={validMatches.length > 0}
          hasSelectedMatch={!!actions.selectedSuggestionId}
          onRestore={actions.handleRestore}
          onToggleGlossary={panels.toggleGlossary}
          onUndoReconcile={actions.handleReconcileUndo}
          onRevert={actions.requestRevert}
          onRemove={removeModal.show}
          onRerun={rerunModal.show}
          onToggleChangeMatch={panels.toggleChangeMatch}
          onReconcile={panels.openReconcile}
          onToggleAgentDetails={panels.toggleAgentDetails}
          onMarkCorrect={markCorrect.show}
        />

        {refreshFailed && (
          <ActionableNotification
            kind="error"
            lowContrast
            inline
            hideCloseButton
            title="Could not refresh this party."
            actionButtonLabel="Retry refresh"
            onActionButtonClick={refetchDetail}
          />
        )}

        {actions.error &&
          (actions.retryProgress ? (
            <ActionableNotification
              kind="error"
              lowContrast
              inline
              title="Error"
              subtitle={actions.error}
              actionButtonLabel="Retry re-run check"
              onActionButtonClick={actions.retryProgress}
              onCloseButtonClick={actions.clearError}
            />
          ) : (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={actions.error}
              onCloseButtonClick={actions.clearError}
            />
          ))}

        {isUserAdded ? (
          <section className={matchesSectionClass}>
            <p className={style.matchesHeader}>Confirmed Entity</p>
            <p className={style.matchesIntro}>
              This party was added by you and is automatically marked complete.
              The linked entity is shown below.
            </p>
            <UserAddedConfirmedCard party={detail} />
          </section>
        ) : (
          <section className={matchesSectionClass}>
            <PartyStatusBanners
              detail={detail}
              state={state}
              reconcileUndoAvailable={actions.reconcileUndoAvailable}
              reconcileResult={actions.banners.reconcileResult}
              reconcileDetailsOpen={actions.banners.reconcileDetailsOpen}
              onToggleReconcileDetails={actions.banners.toggleReconcileDetails}
              markCorrectAlias={actions.banners.markCorrectAlias}
              confidenceStale={actions.banners.confidenceStale}
              onDismissConfidenceStale={actions.banners.dismissConfidenceStale}
            />
            <MatchReviewIntro
              state={state}
              rerunPending={actions.rerunPending}
            />
            <PartyMatchesPane
              detail={detail}
              state={state}
              validMatches={validMatches}
              selectedSuggestionId={actions.selectedSuggestionId}
              pending={actions.pending}
              onSelectSuggestion={actions.pickSuggestion}
              onEditStaged={panels.openStagedEditor}
              onCanonize={panels.openCanonize}
              onSelectEntityMatch={panels.openChangeMatch}
            />
          </section>
        )}

        <section className={style.auditSection}>
          <ActivityLog activity={activity} emptyMessage="No activity yet." />
        </section>

        <PartyConfirmModals
          detail={detail}
          actions={actions}
          hasUnresolvedAddressFlag={hasUnresolvedAddressFlag}
        />

        <PartySidePanels
          document={document}
          detail={detail}
          allParties={allParties}
          actions={actions}
          validMatches={validMatches}
          partyLabel={`Party ${detail.partyNumber}${roleSuffix}`}
        />
      </article>
    );
  };

  return (
    <>
      <AtomisedNotifications groupIds={[NotificationGroup.StructuredParties]} />
      {renderParty()}
    </>
  );
}

interface PartyConfirmModalsProps {
  detail: StructuredPartyDetail;
  actions: ReadyPartyActions;
  hasUnresolvedAddressFlag: boolean;
}

/**
 * The confirms a party's actions open: the flag override, Mark as Correct, Remove, Remove Correct
 * Status and Re-run.
 *
 * @param {PartyConfirmModalsProps} props - The party, its actions and its address-flag state.
 * @return {JSX.Element} The modals.
 */
function PartyConfirmModals({
  detail,
  actions,
  hasUnresolvedAddressFlag,
}: PartyConfirmModalsProps) {
  const { markCorrect, flagOverride, removeModal, rerunModal, revertModal } =
    actions;
  return (
    <>
      <FlagOverrideModal
        open={!!flagOverride.flagged}
        flaggedEntities={flagOverride.flagged ?? []}
        loading={actions.confirmLoading}
        error={actions.dialogError}
        onCancel={flagOverride.cancel}
        onConfirm={(rationale, falsePositiveIds) =>
          flagOverride.confirm(rationale, falsePositiveIds)
        }
        renderRowGraphLink={(id) => <RowAliasGraphLink entityId={id} />}
        floatingMenuSelectors={[ALIAS_CLUSTER_GRAPH_SELECTOR]}
      />

      <MarkCorrectModal
        open={markCorrect.open}
        anchorName={anchorName(detail)}
        pending={actions.pending}
        hasUnresolvedAddressFlag={hasUnresolvedAddressFlag}
        sharedWith={detail.sharedWith}
        preserveStaged={markCorrect.preserveStaged}
        aliasReason={markCorrect.aliasReason}
        error={flagOverride.flagged ? null : actions.dialogError}
        onPreserveStagedChange={markCorrect.setPreserveStaged}
        onAliasReasonChange={markCorrect.setAliasReason}
        onSubmit={markCorrect.submit}
        onClose={markCorrect.close}
      />

      {/* Portaled after the side panels, which share the modal's z-index, so
        a confirm opened with a panel open stacks above it. */}
      {createPortal(
        <>
          <Modal
            open={removeModal.open}
            danger
            modalHeading="Remove Party"
            primaryButtonText="Remove Party"
            secondaryButtonText="Cancel"
            onRequestSubmit={removeModal.submit}
            onRequestClose={removeModal.close}
            primaryButtonDisabled={actions.pending}
          >
            {actions.dialogError && (
              <InlineNotification
                kind="error"
                lowContrast
                hideCloseButton
                title="Error"
                subtitle={actions.dialogError}
                className={style.confirmNotification}
              />
            )}
            <p>
              Are you sure you want to remove {detail.name} from the structured
              provisions for this document?
            </p>
          </Modal>

          <Modal
            open={revertModal.open}
            modalHeading="Remove Correct Status"
            primaryButtonText="Remove Correct Status"
            secondaryButtonText="Cancel"
            onRequestSubmit={revertModal.submit}
            onRequestClose={revertModal.close}
            primaryButtonDisabled={actions.pending}
          >
            <SharedAnchorNotice sharedWith={detail.revertSharedWith} />
            <p>
              Remove the correct status from{' '}
              {endSentence(anchorName(detail) || EMPTY_CELL)} The party returns
              to its previous match.
            </p>
          </Modal>

          <Modal
            open={rerunModal.open}
            modalHeading="Re-run Match Suggestions"
            primaryButtonText="Re-run Match Suggestions"
            secondaryButtonText="Cancel"
            onRequestSubmit={rerunModal.submit}
            onRequestClose={rerunModal.close}
            primaryButtonDisabled={actions.pending || actions.rerunPending}
          >
            <SharedAnchorNotice sharedWith={detail.sharedWith} />
            <p>
              Re-run matching for this party to generate fresh suggestions for{' '}
              {endSentence(anchorName(detail) || EMPTY_CELL)} Existing pending
              suggestions will be superseded.
            </p>
          </Modal>
        </>,
        window.document.body
      )}
    </>
  );
}

interface PartySidePanelsProps {
  document: DocumentResponse;
  detail: StructuredPartyDetail;
  allParties: StructuredPartyListItem[];
  actions: ReadyPartyActions;
  validMatches: SuggestionSummary[];
  partyLabel: string;
}

/**
 * The side panels a party opens: change match, canonize, the staged entity editor, the reconcile
 * tearsheet, the scoring glossary and the agent details.
 *
 * @param {PartySidePanelsProps} props - The party and its actions.
 * @return {JSX.Element} The panels.
 */
function PartySidePanels({
  document,
  detail,
  allParties,
  actions,
  validMatches,
  partyLabel,
}: PartySidePanelsProps) {
  const { panels } = actions;
  // The reconcile candidate set is the anchor plus every distinct match
  // candidate.
  const reconcileCandidates = useMemo(
    () => buildReconcileCandidates(detail),
    [detail]
  );

  const anchorId = anchorCard(detail)?.id ?? null;
  return (
    <>
      <ChangeMatchPanel
        open={panels.changeMatchOpen}
        document={document}
        party={detail}
        allParties={allParties}
        onClose={panels.closeChangeMatch}
        onSaved={actions.outcomes.changed(() => {
          panels.closeAll();
          actions.afterMutation();
        })}
        onMutated={actions.outcomes.changed(actions.afterMutation)}
      />

      <CanonizePanel
        open={panels.canonizeOpen}
        document={document}
        party={detail}
        sessionId={actions.reconcileSession}
        onClose={panels.closeCanonize}
        onCanonized={actions.outcomes.succeeded(
          { id: 'canonized', message: 'Entity added and marked as correct' },
          () => {
            panels.closeAll();
            actions.afterMutation();
          }
        )}
      />

      <StagedEntityEditorPanel
        open={panels.stagedEditorOpen}
        document={document}
        party={detail}
        allParties={allParties}
        onClose={actions.forThisParty(panels.closeStagedEditor)}
        onSaved={actions.outcomes.changed(actions.afterMutation)}
      />

      <ReconcileTearsheet
        open={panels.reconcileOpen}
        onClose={panels.closeReconcile}
        documentId={document.id}
        partyId={detail.id}
        sessionId={actions.reconcileSession}
        partyLabel={partyLabel}
        anchorId={anchorId}
        candidates={reconcileCandidates}
        sharedWith={detail.sharedWith}
        onComplete={actions.handleReconcileComplete}
        onResolutionChanged={actions.handleReconcileResolutionChanged}
      />

      <ScoringGlossaryPanel
        open={panels.glossaryOpen}
        onClose={panels.closeGlossary}
      />

      <AgentDetailsPanel
        open={panels.agentDetailsOpen}
        onClose={panels.closeAgentDetails}
        details={detail.agentDetails}
        autoMatch={detail.autoMatch}
        suggestions={validMatches}
        partyName={detail.name}
        partyId={anchorId}
      />
    </>
  );
}
